import React, { useState, useEffect } from "react";
import { weekendScheduleService } from "../../../services/api";

const emptyForm = () => ({ dayHeading: "", daySubheading: "", description: "" });

const AdminWeekendSchedule = ({ weekend }) => {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm());
  const [currentId, setCurrentId] = useState(null);
  const [message, setMessage]   = useState(null);

  useEffect(() => { if (weekend?.id) fetchSchedule(); }, [weekend?.id]);

  const flash = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const response = await weekendScheduleService.getAll(weekend.id);
      setSchedule(response.data.sort((a, b) => a.orderID - b.orderID));
    } catch {
      setError("Не вдалося завантажити розклад");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => { setFormData(emptyForm()); setFormMode("add"); setCurrentId(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    try {
      if (formMode === "add") {
        await weekendScheduleService.create(weekend.id, formData);
        flash("success", "Запис успішно додано!");
      } else {
        await weekendScheduleService.update(weekend.id, currentId, formData);
        flash("success", "Запис успішно оновлено!");
      }
      resetForm();
      fetchSchedule();
    } catch {
      flash("error", `Помилка при ${formMode === "add" ? "додаванні" : "оновленні"} запису`);
    }
  };

  const handleEdit = (day) => {
    setFormData({ dayHeading: day.dayHeading, daySubheading: day.daySubheading, description: day.description });
    setCurrentId(day.orderID);
    setFormMode("edit");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Видалити цей запис?")) return;
    try {
      await weekendScheduleService.delete(weekend.id, id);
      setSchedule((prev) => prev.filter((item) => item.orderID !== id));
      flash("success", "Запис успішно видалено!");
    } catch {
      flash("error", "Помилка при видаленні запису");
    }
  };

  const moveItem = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= schedule.length) return;
    try {
      await weekendScheduleService.reorder(weekend.id, schedule[index].orderID, newIndex);
      flash("success", "Порядок успішно змінено!");
      fetchSchedule();
    } catch {
      flash("error", "Помилка при зміні порядку");
    }
  };

  const msgColors = { success: ["#d4edda","#155724"], error: ["#f8d7da","#721c24"], info: ["#cce5ff","#004085"] };

  if (loading) return <p style={styles.hint}>Завантаження...</p>;
  if (error)   return <p style={{ color: "#dc3545" }}>{error}</p>;

  return (
    <div>
      {message && (
        <div style={{ ...styles.message, backgroundColor: msgColors[message.type]?.[0], color: msgColors[message.type]?.[1] }}>
          {message.text}
        </div>
      )}

      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>{formMode === "add" ? "Додати новий день" : "Редагувати день"}</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Заголовок дня:</label>
            <input style={styles.input} name="dayHeading" placeholder="День 1"
              value={formData.dayHeading} onChange={(e) => setFormData((p) => ({ ...p, dayHeading: e.target.value }))} required />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Підзаголовок:</label>
            <input style={styles.input} name="daySubheading" placeholder="Четвер"
              value={formData.daySubheading} onChange={(e) => setFormData((p) => ({ ...p, daySubheading: e.target.value }))} required />
          </div>
          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
            <label style={styles.label}>Опис активностей:</label>
            <textarea style={styles.textArea} name="description" value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))} required />
          </div>
          <div style={styles.buttonContainer}>
            {formMode === "edit" && (
              <button type="button" style={styles.cancelButton} onClick={resetForm}>Скасувати</button>
            )}
            <button type="submit" style={styles.button}>
              {formMode === "add" ? "Додати" : "Зберегти зміни"}
            </button>
          </div>
        </form>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              {["Порядок","Заголовок","Підзаголовок","Опис","Дії"].map((h) => (
                <th key={h} style={styles.tableHeader}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {schedule.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: "center", padding: "2rem" }}>Немає записів</td></tr>
            ) : schedule.map((item, index) => (
              <tr key={item.orderID} style={styles.tableRow}>
                <td style={styles.tableCell}>
                  <div style={styles.positionButtons}>
                    <button style={styles.positionButton} onClick={() => moveItem(index, -1)} disabled={index === 0}>▲</button>
                    <button style={styles.positionButton} onClick={() => moveItem(index, 1)} disabled={index === schedule.length - 1}>▼</button>
                  </div>
                </td>
                <td style={styles.tableCell}>{item.dayHeading}</td>
                <td style={styles.tableCell}>{item.daySubheading}</td>
                <td style={styles.tableCell}><div style={styles.descriptionCell}>{item.description}</div></td>
                <td style={styles.tableCell}>
                  <a style={styles.actionLink} onClick={() => handleEdit(item)}>Редагувати</a>
                  <a style={styles.actionLinkDanger} onClick={() => handleDelete(item.orderID)}>Видалити</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  hint: { padding: "2rem", textAlign: "center", color: "#666" },
  message: { padding: "1rem", marginBottom: "1rem", borderRadius: "4px" },
  formContainer: { marginBottom: "2rem", padding: "1.5rem", backgroundColor: "#f9f9f9", borderRadius: "8px" },
  formTitle: { marginBottom: "1rem", fontSize: "1.3rem", color: "var(--secondary-color)" },
  form: { display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" },
  formGroup: { display: "flex", flexDirection: "column" },
  label: { marginBottom: "0.5rem", fontWeight: 600 },
  input: { padding: "0.8rem", border: "1px solid #ddd", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", outline: "none" },
  textArea: { padding: "0.8rem", border: "1px solid #ddd", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", minHeight: "100px", resize: "vertical", outline: "none" },
  button: { padding: "0.8rem 1.5rem", backgroundColor: "green", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontWeight: 500, cursor: "pointer" },
  cancelButton: { padding: "0.8rem 1.5rem", backgroundColor: "#FFBB00", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontWeight: 500, cursor: "pointer", marginRight: "1rem" },
  buttonContainer: { display: "flex", justifyContent: "flex-end", gridColumn: "1 / -1" },
  tableContainer: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  tableHeader: { padding: "0.8rem", textAlign: "left", borderBottom: "1px solid #eee", backgroundColor: "#e8e8e8", fontWeight: 600 },
  tableRow: { borderBottom: "1px solid #eee" },
  tableCell: { padding: "0.8rem", textAlign: "left" },
  descriptionCell: { maxWidth: "300px",  whiteSpace: "pre-wrap" },
  actionLink: { color: "var(--primary-color)", cursor: "pointer", marginRight: "1rem" },
  actionLinkDanger: { color: "var(--error-color)", cursor: "pointer" },
  positionButtons: { display: "flex", flexDirection: "column", gap: "0.25rem" },
  positionButton: { width: "30px", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "#f0f0f0", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer", fontSize: "12px", padding: 0 },
};

export default AdminWeekendSchedule;